/**
 * Contains general safegaurds for accepting donations or updating the cart.
 */
import type { Candidate, Organization } from '../types';

/**
 * Gateway safegaurd.
 * Ensures that the gateway_id is a number and exists.
 */
export const gatewaySafeGaurd = (target: Candidate|Organization): boolean => {
	if (!target?.gateway_id) {
		return false;
	}

	if (typeof target.gateway_id !== 'number') {
		return false;
	}

	return true;
};

/**
 * Check candidate election date offset.
 */
export const electionDateSafeGaurd = (candidate: Candidate): boolean => {
	// Allow runoff override.
	if (candidate.candidate_runoff) {
		return true;
	}

	if (candidate.election_blackout) {
		const now = new Date();
		const cEnd = new Date(candidate.election_blackout * 1000);
		if (now.getTime() > cEnd.getTime()) {
			return false;
		}
	} else {
		return false;
	}

	return true;
};

/**
 * Candidate safegaurds.
 * Handle cases where candidates should not be able to be selected.
 */
export const candidateSafeGaurd = (candidate: Candidate) => {
	/**
	 * Black out notice.
	 */
	if (candidate.candidate_blackout) {
		return false;
	}

	/**
	 * Election date.
	 */
	if (!electionDateSafeGaurd(candidate)) {
		return false;
	}

	return true;
};

/**
 * General safegaurd.
 * Handles both Candidate and Organization safegaurds.
 * @param target Candidate or Organization
 */
export const safegaurd = (target: Candidate|Organization): boolean => {
	if (!gatewaySafeGaurd(target)) {
		return false;
	}

	if (target.isCandidate) {
		return candidateSafeGaurd(target);
	}

	return true;
};
